/**
 * Global front-end script.
 *
 * Compiled to dist/assets/js/global.js and enqueued on both the front end and
 * in the editor — but the editor takes the early-exit branch below, so none of
 * the scroll or animation machinery runs there.
 *
 * GSAP, ScrollTrigger and ScrollToPlugin are loaded separately (see
 * wpbase_enqueue_assets in inc/general.php) and referenced as globals.
 */

const AOS = require("aos");
const Lenis = require("lenis").default || require("lenis");
require("select2");

// Must stay in step with $header-breakpoint in abstracts/_mixins.scss.
const HEADER_BREAKPOINT = 992;

function isWordPressEditor() {
  return (
    typeof window !== "undefined" &&
    (window.location.pathname.includes("/wp-admin/") ||
      window.frameElement !== null ||
      document.body.classList.contains("block-editor-page") ||
      document.getElementById("editor") !== null ||
      (typeof wp !== "undefined" && wp.blocks !== undefined))
  );
}

if (isWordPressEditor()) {
  // ── Editor ──────────────────────────────────────────────────────────────
  // Block previews can contain <select> elements that should look like they do
  // on the front end. Nothing else here belongs in the editor.

  jQuery(document).ready(function ($) {
    const selectOptions = {
      minimumResultsForSearch: Infinity,
      placeholder: "Select",
    };

    // Scoped to previews so ACF's own field UI is left alone.
    $(".acf-block-preview select").select2(selectOptions);

    if (window.acf) {
      window.acf.addAction("render_block_preview", function () {
        // A short delay lets ACF finish swapping the preview markup in.
        setTimeout(function () {
          $(".acf-block-preview select")
            .not(".select2-hidden-accessible")
            .select2(selectOptions);
        }, 100);
      });
    }
  });
} else {
  // ── Front end ───────────────────────────────────────────────────────────

  // Dispatched on a section once the page has finished scrolling to it, from
  // an anchor link or from a hash in the URL. Blocks listen for it when
  // arriving should do more than move the page — see
  // blocks/content/guide-download, which opens its form popup on it.
  const HASH_TARGET_EVENT = "wpbase:hash-target";

  // Dispatched, cancelable, on the target before scrolling to it. A block
  // whose content does not sit where the layout puts it cancels it and takes
  // the reader there itself, announcing arrival with HASH_TARGET_EVENT too —
  // see blocks/content/content-rows, whose pinned rows all share the
  // section's position and slide in sideways.
  const SCROLL_REQUEST_EVENT = "wpbase:scroll-request";

  // See holdScroll() below for what the hold means and who takes one.
  const SCROLL_HOLD_ATTRIBUTE = "data-wpbase-scroll-hold";
  const SCROLL_RELEASED_EVENT = "wpbase:scroll-released";

  // Seconds, shared by anchor clicks and the hash-on-load scroll.
  const SCROLL_DURATION = 1.5;

  // How long past `load` the hash scroll waits before measuring. Content Rows
  // rebuilds its pinned timeline 150ms after `load` and AOS re-measures around
  // then too; scrolling any sooner aims at a position they go on to move.
  const HASH_SCROLL_SETTLE = 350;

  // getElementById rather than querySelector: block IDs are free-form, and one
  // like "2-column" makes "#2-column" an invalid selector, which throws.
  function targetFromHash(hash) {
    if (!hash || hash.length < 2) return null;

    // A trailing slash is part of the fragment, so "#guide/" matches nothing
    // — not even for the browser's own jump. WordPress hands one out of custom
    // menu links often enough to be worth forgiving, and sanitize_title() never
    // leaves a slash in a block ID for it to be confused with.
    let id = hash.slice(1).replace(/\/+$/, "");

    try {
      id = decodeURIComponent(id);
    } catch (e) {
      // A malformed escape sequence — look the raw id up instead.
    }

    return id ? document.getElementById(id) : null;
  }

  // The browser jumps straight to a hash target the moment it finds it, which
  // leaves the section sitting under the sticky header and skips the scroll
  // entirely. This script is deferred, so this is the first chance to undo
  // that jump; the scroll that replaces it runs from the DOM-ready handler
  // below, once the page has settled enough to measure.
  const hasInitialHashTarget = targetFromHash(window.location.hash) !== null;

  if (hasInitialHashTarget) {
    // Otherwise a reload restores its own scroll position on top of ours.
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);
  }

  document.addEventListener("DOMContentLoaded", function () {
    // Match Selecta's cookie-preferences control when Usercentrics is available.
    // Without a consent manager, the link still opens the published Cookie Notice.
    document.querySelectorAll("[data-footer-cookies]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (typeof window.UC_UI?.showSecondLayer === "function") {
          event.preventDefault();
          window.UC_UI.showSecondLayer();
        }
      });
    });

    // ── Lenis smooth scroll ───────────────────────────────────────────────
    // Skipped on mobile (native momentum scrolling is better there) and when
    // the user has asked for reduced motion.

    const isMobile = window.innerWidth < 768;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let lenis = null;

    if (!isMobile && !prefersReducedMotion) {
      lenis = new Lenis({
        lerp: 0.1,
        smoothWheel: true,
        wheelMultiplier: 1,
      });

      if (typeof gsap !== "undefined") {
        // Drive Lenis from GSAP's ticker so the page runs one RAF loop, not two.
        gsap.ticker.add((time) => {
          lenis.raf(time * 1000);
        });
        gsap.ticker.lagSmoothing(0);
      } else {
        const raf = (time) => {
          lenis.raf(time);
          requestAnimationFrame(raf);
        };
        requestAnimationFrame(raf);
      }
    }

    // Blocks that need scroll position can read window.lenis.
    window.lenis = lenis;

    // ── Cookie banner ─────────────────────────────────────────────────────
    // Usercentrics locks the page while its banner is open (this class on
    // <body>), and on closing scrolls back to wherever the page sat when the
    // banner opened. Lenis and the anchor scrolling keep the page moving
    // underneath it, so by the time the visitor chooses that is often the
    // top. Put them back where they were when they chose.
    const CONSENT_LOCK_CLASS = "overflowHidden";
    let lastScrollTop = window.scrollY;
    let consentLocked = document.body.classList.contains(CONSENT_LOCK_CLASS);

    window.addEventListener(
      "scroll",
      () => {
        lastScrollTop = window.scrollY;
      },
      { passive: true },
    );

    new MutationObserver(() => {
      const locked = document.body.classList.contains(CONSENT_LOCK_CLASS);

      // The scroll back has just been asked for, and neither Lenis nor
      // lastScrollTop has seen it yet. An instant scroll of our own stops it:
      // a smooth one (CSS scroll-behavior) before it has moved, an instant
      // one before it is painted. Where nothing moved, this changes nothing.
      if (consentLocked && !locked) {
        window.scrollTo({
          top: lenis ? lenis.animatedScroll : lastScrollTop,
          behavior: "instant",
        });
      }

      consentLocked = locked;
    }).observe(document.body, { attributes: true, attributeFilter: ["class"] });

    // ── Sticky header ─────────────────────────────────────────────────────
    // The header only pins once the user is well past it, then slides in. The
    // hide threshold is much lower than the show threshold so scrolling up and
    // down around one point cannot make it flicker.

    const header = document.querySelector(".header");

    if (header) {
      let headerHeight = 0;
      let isSticky = false;
      let headerHeightUpdatePending = false;

      function updateHeaderHeight() {
        if (headerHeightUpdatePending) return;
        headerHeightUpdatePending = true;

        requestAnimationFrame(() => {
          headerHeight = header.offsetHeight;
          headerHeightUpdatePending = false;
        });
      }

      function updateStickyHeader(scrollY) {
        if (!headerHeight) {
          updateHeaderHeight();
        }

        const showThreshold = headerHeight + 300;
        const hideThreshold = 300;

        if (scrollY > showThreshold) {
          if (!isSticky) {
            header.classList.add("header--sticky");
            header.classList.remove("header--show");

            jQuery(header)
              .stop(true, true)
              .hide()
              .slideDown(280, function () {
                header.classList.add("header--show");
                header.style.display = "";
              });

            isSticky = true;
          }
        } else if (scrollY < hideThreshold && isSticky) {
          jQuery(header)
            .stop(true, true)
            .slideUp(220, function () {
              header.classList.remove("header--show", "header--sticky");
              header.style.display = "";
            });

          isSticky = false;
        }
      }

      updateHeaderHeight();

      if (lenis) {
        lenis.on("scroll", (instance) => {
          updateStickyHeader(instance.animatedScroll);
        });
      } else {
        window.addEventListener(
          "scroll",
          () => updateStickyHeader(window.scrollY),
          { passive: true },
        );
      }

      window.addEventListener(
        "resize",
        () => {
          updateHeaderHeight();
          updateStickyHeader(lenis ? lenis.animatedScroll : window.scrollY);
        },
        { passive: true },
      );

      updateStickyHeader(window.scrollY);
    }

    // ── Mobile menu ───────────────────────────────────────────────────────
    // `show-mobile-menu` on <body> drives the drawer; see layout/_header.scss.

    const menuToggles = document.querySelectorAll(".header__menu-toggle");

    if (menuToggles.length) {
      menuToggles.forEach((toggle) => {
        toggle.addEventListener("click", function () {
          const isOpen = document.body.classList.toggle("show-mobile-menu");

          menuToggles.forEach((t) => {
            t.setAttribute("aria-expanded", isOpen ? "true" : "false");
          });
        });
      });

      // Close on Escape, and on resize back up to the full nav — otherwise the
      // drawer state persists into a layout that has no way to dismiss it.
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && document.body.classList.contains("show-mobile-menu")) {
          document.body.classList.remove("show-mobile-menu");
          menuToggles.forEach((t) => t.setAttribute("aria-expanded", "false"));
        }
      });

      window.addEventListener(
        "resize",
        () => {
          if (window.innerWidth > HEADER_BREAKPOINT) {
            document.body.classList.remove("show-mobile-menu");
            menuToggles.forEach((t) => t.setAttribute("aria-expanded", "false"));
          }
        },
        { passive: true },
      );
    }

    // ── Scroll animations ─────────────────────────────────────────────────
    // Opt in from markup:
    //   data-speed="0.5"  parallax, relative to scroll
    //   data-float="20"   idle float, in px
    //   data-reveal       fade and rise into view

    if (typeof gsap !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);

      // Keep ScrollTrigger's positions in step with Lenis's virtual scroll.
      if (lenis) {
        lenis.on("scroll", ScrollTrigger.update);
      }

      gsap.utils.toArray("[data-speed]").forEach((element) => {
        const speed = parseFloat(element.dataset.speed) || 0.5;

        gsap.to(element, {
          yPercent: -50 * speed,
          ease: "none",
          scrollTrigger: {
            trigger: element,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.5,
          },
        });
      });

      gsap.utils.toArray("[data-float]").forEach((element) => {
        const intensity = parseFloat(element.dataset.float) || 20;

        gsap.to(element, {
          y: `random(-${intensity}, ${intensity})`,
          rotation: "random(-2, 2)",
          duration: "random(3, 5)",
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          delay: "random(0, 2)",
        });
      });

      gsap.utils.toArray("[data-reveal]").forEach((element) => {
        gsap.fromTo(
          element,
          { opacity: 0, y: 50, scale: 0.98 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: element,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    } else {
      console.warn("GSAP not loaded — scroll animations disabled");
    }

    // ── Smooth anchor scrolling ───────────────────────────────────────────
    // Lands the whole of the target section below the header.
    //
    // The header is in the flow at the top of the page and `position: fixed`
    // once it goes sticky, on tighter padding (layout/_header.scss). A scroll
    // of any real distance ends with it sticky, so the landing spot has to be
    // worked out in that layout rather than this one — two corrections, not
    // one: everything below the header sits a full header height higher once
    // the header leaves the flow, and the band left covering the top of the
    // viewport is the shorter sticky one.

    // Breathing room between the header and the top of the section.
    const HEADER_GAP = 20;

    let headerHeights = { flow: 0, sticky: 0 };

    // One of the two states can be measured directly; the other is measured on
    // a clone. Putting the class on the real header and taking it off again
    // would be cheaper, but Content Rows watches the header for class changes
    // and rebuilds its pinned timeline on each one, which would fight the
    // scroll being set up here.
    function measureHeaderHeights() {
      const header = document.querySelector(".header");

      // jQuery hides the header while sliding it into view, and a hidden
      // element measures 0. Keep the last good pair rather than storing that.
      if (!header || !header.offsetHeight) return;

      const isSticky = header.classList.contains("header--sticky");
      const clone = header.cloneNode(true);

      clone.classList.toggle("header--sticky", !isSticky);
      clone.setAttribute("aria-hidden", "true");

      // Fixed and sized to the real header: out of the flow, so inserting it
      // cannot move the page, and wrapping the same way, so the height it
      // reports is the height the real one would have.
      clone.style.cssText =
        "position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;width:" +
        header.offsetWidth +
        "px";

      header.parentNode.insertBefore(clone, header);
      const cloned = clone.offsetHeight;
      clone.remove();

      headerHeights = isSticky
        ? { flow: cloned, sticky: header.offsetHeight }
        : { flow: header.offsetHeight, sticky: cloned };
    }

    requestAnimationFrame(measureHeaderHeights);
    window.addEventListener(
      "resize",
      () => requestAnimationFrame(measureHeaderHeights),
      { passive: true },
    );

    // `data-wpbase-scroll-hold` on <html> means something owns where the page
    // is sitting and nothing else may move it: a scroll from here on its way
    // somewhere, or a popup covering the page. Content Rows watches for it,
    // because rebuilding its pinned timeline repositions the page and
    // re-measures the layout — ending a scroll early if one is running, and
    // measuring a page that cannot scroll if a popup has locked it.
    //
    // Both can hold at once, so it is a count, and the page is only free again
    // once the last of them has let go.
    let scrollHolds = 0;

    function holdScroll() {
      scrollHolds += 1;
      document.documentElement.setAttribute(SCROLL_HOLD_ATTRIBUTE, "");
    }

    function releaseScroll() {
      if (scrollHolds === 0) return;

      scrollHolds -= 1;

      if (scrollHolds > 0) return;

      document.documentElement.removeAttribute(SCROLL_HOLD_ATTRIBUTE);
      document.dispatchEvent(new CustomEvent(SCROLL_RELEASED_EVENT));
    }

    // Blocks that cover the page hold it too — see blocks/content/guide-download.
    // restingScrollTop is for blocks that scroll to their own content (see
    // SCROLL_REQUEST_EVENT), so they land clear of the header the same way.
    window.wpbaseScroll = {
      hold: holdScroll,
      release: releaseScroll,
      restingScrollTop,
    };

    // The visitor taking over ends the settle: re-aiming under them would be
    // a fight rather than a scroll.
    const INTERRUPT_EVENTS = ["wheel", "touchstart", "keydown"];

    // The settle below re-aims until the page comes to rest on the target.
    // This caps how long it will keep trying before giving up and announcing.
    const SETTLE_TIMEOUT = 6000;

    // Seconds. Reduced motion gets there without the travel.
    const scrollDuration = (isLongHop) => {
      if (prefersReducedMotion) return 0;
      return isLongHop ? SCROLL_DURATION : 0.4;
    };

    // Where the page has to sit for the whole of the section to clear the
    // header, in the layout as it stands at the moment it is asked. `gap` is
    // the space left between the two.
    function restingScrollTop(targetElement, gap = HEADER_GAP) {
      const header = document.querySelector(".header");
      const headerInFlow =
        header !== null && !header.classList.contains("header--sticky");

      // The header leaves the flow on the way down, and everything below it
      // rises by its full height when it does. Anticipating that is what keeps
      // the section from landing a header's height too low.
      const documentTop =
        targetElement.getBoundingClientRect().top +
        window.scrollY -
        (headerInFlow ? headerHeights.flow : 0);

      return Math.max(0, documentTop - headerHeights.sticky - gap);
    }

    // Ends the scroll still settling, if there is one. Left running it would
    // go on re-aiming at its own target underneath a newer scroll.
    let cancelActiveScroll = null;

    function scrollToTarget(targetElement) {
      cancelActiveScroll?.();
      cancelActiveScroll = null;

      const request = new CustomEvent(SCROLL_REQUEST_EVENT, {
        bubbles: true,
        cancelable: true,
      });

      if (!targetElement.dispatchEvent(request)) return;

      let announced = false;
      let holding = false;
      let settleTimer = null;
      let interrupted = false;

      const giveUpAt = Date.now() + SETTLE_TIMEOUT;
      const noteInterrupt = () => {
        interrupted = true;
      };

      INTERRUPT_EVENTS.forEach((type) =>
        window.addEventListener(type, noteInterrupt, { passive: true }),
      );

      // This scroll's own hold, taken once however many times it aims.
      function holdPage() {
        if (holding) return;
        holding = true;
        holdScroll();
      }

      function releasePage() {
        if (!holding) return;
        holding = false;
        releaseScroll();
      }

      // Announced once, with the page at rest, so that whatever is listening
      // — the Guide Download popup — opens over a page that has stopped.
      function announceArrival() {
        if (announced) return;
        announced = true;

        clearTimeout(settleTimer);
        INTERRUPT_EVENTS.forEach((type) =>
          window.removeEventListener(type, noteInterrupt),
        );

        // Announced before this scroll lets go, so a block that opens over the
        // page takes its own hold while this one is still on and the page is
        // never unowned in between. Work waiting on the hold then runs once
        // the page is free and scrollable again, which for a popup means once
        // it has been closed.
        targetElement.dispatchEvent(
          new CustomEvent(HASH_TARGET_EVENT, { bubbles: true }),
        );

        releasePage();
      }

      // Re-measured on every aim rather than worked out once: the pinned
      // Content Rows block rebuilds itself as the sticky header appears, and
      // the page moves when it does.
      function aim(duration) {
        holdPage();

        const top = restingScrollTop(targetElement);

        if (duration === 0) {
          if (lenis) {
            lenis.scrollTo(top, { immediate: true });
          } else {
            window.scrollTo({ top, behavior: "auto" });
          }
        } else if (lenis) {
          lenis.scrollTo(top, { duration });
        } else if (typeof gsap !== "undefined" && gsap.plugins.ScrollToPlugin) {
          gsap.to(window, {
            duration,
            scrollTo: { y: top, autoKill: false },
            ease: "power2.inOut",
          });
        } else {
          window.scrollTo({ top, behavior: "smooth" });
        }

        // Each pass waits out its own animation before judging where it got
        // to, so the aims cannot stack up and restart one another.
        settleTimer = setTimeout(settle, duration * 1000 + 120);
      }

      // Neither animation can be trusted to report arriving: anything that
      // forces a scroll position of its own ends them early and silently. So
      // the page is checked against the target, and aimed again until it is
      // resting on it.
      function settle() {
        if (announced) return;

        if (interrupted || Date.now() > giveUpAt) {
          announceArrival();
          return;
        }

        const drift = window.scrollY - restingScrollTop(targetElement);

        // A pixel or two is rounding, not a miss.
        if (Math.abs(drift) <= 2) {
          announceArrival();
          return;
        }

        // A short hop to finish, unless the page was knocked far enough off
        // that closing the gap deserves a proper scroll again.
        aim(scrollDuration(Math.abs(drift) > window.innerHeight));
      }

      // Superseded: nothing to announce, but the hold and listeners still go.
      cancelActiveScroll = () => {
        if (announced) return;
        announced = true;

        clearTimeout(settleTimer);
        INTERRUPT_EVENTS.forEach((type) =>
          window.removeEventListener(type, noteInterrupt),
        );
        releasePage();
      };

      aim(scrollDuration(true));
    }

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      const href = anchor.getAttribute("href");

      // "#" alone is a placeholder link, not an anchor target.
      if (!href || href === "#") return;

      anchor.addEventListener("click", function (e) {
        const targetElement = targetFromHash(href);

        if (!targetElement) return;

        e.preventDefault();
        scrollToTarget(targetElement);
      });
    });

    // ── Hash on load ──────────────────────────────────────────────────────
    // The browser's own jump was undone at the top of this file; this is the
    // scroll that replaces it, so that /page/#section reads the same way as
    // clicking a link to it.

    if (hasInitialHashTarget) {
      const scrollToInitialHash = () =>
        setTimeout(() => {
          const targetElement = targetFromHash(window.location.hash);

          if (!targetElement) return;

          measureHeaderHeights();

          // The browser can jump to the target again while the page loads, so
          // start from the top however the page got here.
          if (lenis) {
            lenis.scrollTo(0, { immediate: true, force: true });
          } else {
            window.scrollTo(0, 0);
          }

          scrollToTarget(targetElement);
        }, HASH_SCROLL_SETTLE);

      // Waiting for `load` lets images size and the pinned blocks claim their
      // scroll distance first — measuring before that aims at a position they
      // go on to move.
      if (document.readyState === "complete") {
        scrollToInitialHash();
      } else {
        window.addEventListener("load", scrollToInitialHash, { once: true });
      }
    }
  });

  // ── Select2 ─────────────────────────────────────────────────────────────

  jQuery(document).ready(function ($) {
    $("select").select2({
      minimumResultsForSearch: Infinity,
      placeholder: "Select",
    });

    // Let the dropdown scroll natively instead of fighting Lenis.
    $(document).on("select2:open", () => {
      const dropdown = document.querySelector(".select2-dropdown");
      if (dropdown) dropdown.setAttribute("data-lenis-prevent", "");
    });

    // Select2 draws its own arrow; swap in the theme's chevron. currentColor
    // means it picks up whatever colour the selection box is using.
    const customSvgArrow = `
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true" focusable="false">
        <path d="M1.2 0.7 5 4.5 8.8 0.7a.65.65 0 1 1 .9.9l-4.25 4.25a.65.65 0 0 1-.9 0L0.3 1.6a.65.65 0 0 1 .9-.9Z" fill="currentColor"/>
      </svg>
    `;

    // Deferred so Select2 has finished building its markup.
    setTimeout(function () {
      $(
        ".select2-container--default .select2-selection--single, .select2-container--default .select2-selection--multiple",
      ).each(function () {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = customSvgArrow;
        wrapper.style.cssText =
          "position:absolute;top:50%;right:12px;transform:translateY(-50%);display:flex;align-items:center;justify-content:center;pointer-events:none;";

        this.style.position = "relative";
        this.appendChild(wrapper);
      });
    }, 100);
  });

  // ── AOS ─────────────────────────────────────────────────────────────────
  // Initialised on DOM ready, not `load`. Every `[data-aos]` element sits at
  // opacity 0 until AOS runs, so waiting for the last image would hold the
  // hero blank for as long as the hero image takes to arrive.
  //
  // AOS measures offsets when it initialises, which is before images have
  // sized, so refresh once they have — and again when the webfonts settle,
  // since the display face changes how tall the headings are. AOS already
  // rebinds resize and orientationchange itself.

  jQuery(function () {
    AOS.init({
      duration: 700,
      once: true,
    });

    window.AOS = AOS;

    jQuery(window).on("load", function () {
      AOS.refresh();
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        AOS.refresh();
      });
    }
  });
}
